import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsScript } from '@/components/AddressAutocomplete';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const counties = [
  'Davidson County', 'Williamson County', 'Rutherford County', 'Wilson County',
  'Sumner County', 'Robertson County', 'Cheatham County', 'Dickson County', 'Maury County',
];

const fadeIn = { initial: { opacity: 0 }, whileInView: { opacity: 1 }, viewport: { once: true } };
const fadeInUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const halfSec = { duration: 0.5 };
const delayedHalfSec = { duration: 0.5, delay: 0.1 };
const mapAnim = { initial: { opacity: 0, scale: 0.98 }, whileInView: { opacity: 1, scale: 1 }, viewport: { once: true } };
const viewportOnce = { once: true };

const countyFadeIn = {
  hidden: { opacity: 0, x: -10 },
  visible: (i) => ({ opacity: 1, x: 0, transition: { duration: 0.3, delay: i * 0.05 } }),
};

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1a1a1a' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a1a' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#333333' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e0e0e' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4a4a4a' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#222222' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6a6a6a' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#222222' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#444444' }] },
];

const SERVICE_CITIES = [
  { name: 'Nashville', lat: 36.1627, lng: -86.7816 },
  { name: 'Brentwood', lat: 36.0331, lng: -86.7828 },
  { name: 'Franklin', lat: 35.9251, lng: -86.8689 },
  { name: 'Murfreesboro', lat: 35.8456, lng: -86.3903 },
  { name: 'Hendersonville', lat: 36.3048, lng: -86.6200 },
  { name: 'Mount Juliet', lat: 36.2001, lng: -86.5186 },
  { name: 'Gallatin', lat: 36.3884, lng: -86.4467 },
  { name: 'Spring Hill', lat: 35.7512, lng: -86.9300 },
  { name: 'Smyrna', lat: 35.9828, lng: -86.5186 },
  { name: 'Lebanon', lat: 36.2081, lng: -86.2911 },
];

function InteractiveMap() {
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [inView, setInView] = useState(false);
  const sentinelRef = useRef(null);

  // Defer Google Maps loading until the section enters the viewport
  useEffect(() => {
    if (!GOOGLE_MAPS_KEY) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || !GOOGLE_MAPS_KEY) return;
    loadGoogleMapsScript().then(() => setMapReady(true));
  }, [inView]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.google?.maps) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 36.1627, lng: -86.7816 },
      zoom: 9,
      styles: MAP_STYLES,
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    // Main marker for Nashville HQ
    new window.google.maps.Marker({
      position: { lat: 36.1627, lng: -86.7816 },
      map,
      title: 'Evolution Electric — Nashville, TN',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#FACC15',
        fillOpacity: 1,
        strokeColor: '#000000',
        strokeWeight: 3,
      },
    });

    // Smaller markers for service cities
    SERVICE_CITIES.forEach(city => {
      if (city.name === 'Nashville') return;
      new window.google.maps.Marker({
        position: { lat: city.lat, lng: city.lng },
        map,
        title: city.name,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 6,
          fillColor: '#FACC15',
          fillOpacity: 0.6,
          strokeColor: '#000000',
          strokeWeight: 2,
        },
      });
    });

    // Service area circle
    new window.google.maps.Circle({
      map,
      center: { lat: 36.1627, lng: -86.7816 },
      radius: 56000,
      fillColor: '#FACC15',
      fillOpacity: 0.05,
      strokeColor: '#FACC15',
      strokeOpacity: 0.3,
      strokeWeight: 1,
    });
  }, [mapReady]);

  // Fallback to iframe if no API key
  if (!GOOGLE_MAPS_KEY) {
    return (
      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d412506.90949!2d-87.05!3d36.17!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8864ec3213eb903d%3A0x7d3fb9d0a1e9daa0!2sNashville%2C+TN!5e0!3m2!1sen!1sus"
        width="100%" height="400" style={{ border: 0 }} allowFullScreen="" loading="lazy"
        referrerPolicy="no-referrer-when-downgrade" title="Evolution Electric Service Area - Nashville TN"
        data-testid="google-maps-embed"
      />
    );
  }

  return (
    <div ref={sentinelRef}>
      <div
        ref={mapRef}
        style={{ width: '100%', height: '400px', background: '#1a1a1a' }}
        data-testid="google-maps-interactive"
      />
    </div>
  );
}

function CountyList() {
  return (
    <div className="grid grid-cols-2 gap-2">
      {counties.map((county, i) => (
        <motion.div key={county} initial="hidden" whileInView="visible" viewport={viewportOnce}
          variants={countyFadeIn} custom={i} className="flex items-center gap-2"
          data-testid={`service-area-${county.toLowerCase().replace(/\s/g, '-')}`}>
          <MapPin className="w-3 h-3 text-yellow-400 flex-shrink-0" />
          <span className="font-body text-sm text-white/80">{county}</span>
        </motion.div>
      ))}
    </div>
  );
}

function ServiceAreaMap() {
  return (
    <motion.div {...mapAnim} transition={halfSec} className="border border-white/20 overflow-hidden">
      <InteractiveMap />
    </motion.div>
  );
}

export default function ServiceAreaSection() {
  return (
    <section data-testid="service-area-section" className="relative py-24 sm:py-32 px-6 bg-black text-white overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img src="https://images.unsplash.com/photo-1669652238761-05a7a40ca30d?w=1600&q=60" alt="electrician near me Nashville TN service area coverage" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <motion.p {...fadeIn} className="font-body text-xs uppercase tracking-[0.2em] text-yellow-400 mb-3">Service Area</motion.p>
            <motion.h2 {...fadeInUp} transition={halfSec} className="font-heading text-3xl sm:text-4xl font-black tracking-tight uppercase mb-6">Serving Greater Nashville</motion.h2>
            <motion.p {...fadeInUp} transition={delayedHalfSec} className="font-body text-sm text-white/60 leading-relaxed mb-8">
              We provide residential, commercial, and industrial electrical services across the Nashville metropolitan area and beyond.
            </motion.p>
            <CountyList />
          </div>
          <div className="lg:col-span-7"><ServiceAreaMap /></div>
        </div>
      </div>
    </section>
  );
}
